import { useEffect, useRef, useState } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { Instagram, Mail } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';

export default function ContactPage() {
  const { t } = useLanguage();
  const heroRef = useRef(null);
  const formRef = useScrollReveal({ delay: 150 });
  const [formData, setFormData] = useState({ name: '', email: '', type: 'general', message: '', demoLink: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    document.title = 'Contact // VVS';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !heroRef.current) return;

    anime.timeline({ easing: 'easeOutExpo' })
      .add({ targets: '.ct-caption', translateY: [20, 0], opacity: [0, 1], duration: 600 })
      .add({ targets: '.ct-title', translateY: [50, 0], opacity: [0, 1], duration: 900 }, '-=300')
      .add({ targets: '.ct-sub', translateY: [20, 0], opacity: [0, 1], duration: 600 }, '-=500');
  }, []);

  // Map form type to subject for Resend
  const getSubjectFromType = (type) => {
    const mapping = {
      'general': 'General Contact',
      'partnerships': 'Partnerships',
      'demo': 'Demos',
    };
    return mapping[type] || 'General Contact';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(`${API_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: getSubjectFromType(formData.type),
          message: formData.message,
          demoLink: formData.demoLink || null,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitted(true);
        // Reset form
        setFormData({ name: '', email: '', type: 'general', message: '', demoLink: '' });
        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!prefersReduced) {
          anime({ targets: '.form-success', translateY: [20, 0], opacity: [0, 1], duration: 700, easing: 'easeOutExpo' });
        }
      } else {
        throw new Error(data.detail || 'Submission failed');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      // Fallback: show mailto link if API fails
      setSubmitError(getCurrentEmail());
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%', background: 'rgba(10,22,40,0.6)',
    border: '1px solid rgba(240,244,248,0.1)', color: '#F0F4F8',
    fontFamily: 'Space Grotesk, sans-serif', fontSize: '14px',
    padding: '14px 16px', outline: 'none',
    transition: 'border-color 0.3s ease', boxSizing: 'border-box',
  };

  const TYPES = [
    { value: 'general', label: t('contact.type_general'), email: 'contact@vaguevisionsound.com' },
    { value: 'partnerships', label: t('contact.type_partnerships'), email: 'partnerships@vaguevisionsound.com' },
    { value: 'demo', label: t('contact.type_demo'), email: 'demos@vaguevisionsound.com' },
  ];

  // Get current email based on selected type
  const getCurrentEmail = () => {
    const currentType = TYPES.find(ty => ty.value === formData.type);
    return currentType ? currentType.email : 'contact@vaguevisionsound.com';
  };

  return (
    <div style={{ position: 'relative', zIndex: 1, paddingTop: '64px', overflowX: 'hidden' }}>

      <section ref={heroRef} style={{ padding: isMobile ? '60px 20px 40px' : '80px 48px 60px' }}>
        <div style={{ maxWidth: '900px' }}>
          <p className="ct-caption caption" style={{ marginBottom: '24px', opacity: 0 }}>{t('contact.caption')}</p>
          <h1 className="ct-title" style={{
            fontFamily: 'Syne, sans-serif', fontWeight: 800,
            fontSize: isMobile ? '42px' : 'clamp(48px, 8vw, 96px)',
            lineHeight: 0.92, letterSpacing: '-0.03em', textTransform: 'uppercase',
            color: '#F0F4F8', margin: '0 0 24px', opacity: 0,
          }}>
            {t('contact.title')}
          </h1>
          <p className="ct-sub" style={{
            fontFamily: 'Space Grotesk, sans-serif', fontSize: isMobile ? '14px' : '16px',
            color: 'var(--vvs-muted)', lineHeight: 1.7, opacity: 0, whiteSpace: 'pre-line',
          }}>
            {t('contact.desc')}
          </p>
        </div>
      </section>

      <section style={{ padding: isMobile ? '0 20px 80px' : '0 48px 100px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: isMobile ? '40px' : '60px' }}>
          <div ref={formRef} className="reveal-el">
            {!submitted ? (
              <form onSubmit={handleSubmit} data-testid="contact-form" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                  <div>
                    <label htmlFor="name" className="caption" style={{ display: 'block', marginBottom: '8px' }}>{t('contact.name')}</label>
                    <input id="name" name="name" type="text" required value={formData.name}
                      onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                      placeholder={t('contact.name')} data-testid="contact-name" style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'rgba(0,240,255,0.4)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(240,244,248,0.1)'}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="caption" style={{ display: 'block', marginBottom: '8px' }}>{t('contact.email')}</label>
                    <input id="email" name="email" type="email" required value={formData.email}
                      onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                      placeholder="your@email.com" data-testid="contact-email" style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'rgba(0,240,255,0.4)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(240,244,248,0.1)'}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="type" className="caption" style={{ display: 'block', marginBottom: '8px' }}>{t('contact.subject')}</label>
                  <select id="type" name="type" value={formData.type}
                    onChange={e => setFormData(p => ({ ...p, type: e.target.value }))}
                    data-testid="contact-type"
                    style={{ ...inputStyle, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}
                    onFocus={e => e.target.style.borderColor = 'rgba(0,240,255,0.4)'}
                    onBlur={e => e.target.style.borderColor = 'rgba(240,244,248,0.1)'}
                  >
                    {TYPES.map(ty => (
                      <option key={ty.value} value={ty.value} style={{ background: '#0A1628', color: '#F0F4F8' }}>
                        {ty.label}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.type === 'demo' && (
                  <div data-testid="demo-link-field">
                    <label htmlFor="demoLink" className="caption" style={{ display: 'block', marginBottom: '8px' }}>{t('contact.demo_link')}</label>
                    <input id="demoLink" name="demoLink" type="url" value={formData.demoLink}
                      onChange={e => setFormData(p => ({ ...p, demoLink: e.target.value }))}
                      placeholder={t('contact.demo_ph')} data-testid="contact-demo-link" style={inputStyle}
                      onFocus={e => e.target.style.borderColor = 'rgba(0,240,255,0.4)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(240,244,248,0.1)'}
                    />
                    <p style={{ fontFamily: 'Space Grotesk', fontSize: '11px', color: 'var(--vvs-subtle)', marginTop: '6px', letterSpacing: '0.04em' }}>
                      {t('contact.demo_hint')}
                    </p>
                  </div>
                )}

                <div>
                  <label htmlFor="message" className="caption" style={{ display: 'block', marginBottom: '8px' }}>{t('contact.message')}</label>
                  <textarea id="message" name="message" required value={formData.message}
                    onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                    data-testid="contact-message" rows={6}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '140px' }}
                    onFocus={e => e.target.style.borderColor = 'rgba(0,240,255,0.4)'}
                    onBlur={e => e.target.style.borderColor = 'rgba(240,244,248,0.1)'}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', paddingTop: '8px' }}>
                  <p style={{ fontFamily: 'Space Grotesk', fontSize: '11px', color: 'var(--vvs-subtle)', letterSpacing: '0.04em' }}>
                    {t('contact.sent_to')} <span style={{ color: 'var(--vvs-cyan)' }}>{getCurrentEmail()}</span>
                  </p>
                  <button 
                    type="submit" 
                    className="btn-metallic btn-metallic-filled" 
                    data-testid="contact-submit" 
                    style={{ cursor: isSubmitting ? 'wait' : 'pointer', opacity: isSubmitting ? 0.7 : 1 }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : t('contact.send')}
                  </button>
                </div>

                {submitError && (
                  <div style={{ 
                    padding: '16px', marginTop: '16px',
                    border: '1px solid rgba(255,100,100,0.3)', 
                    background: 'rgba(255,100,100,0.05)',
                    borderRadius: '4px',
                  }}>
                    <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', color: 'rgba(255,150,150,0.9)', marginBottom: '8px' }}>
                      Something went wrong. Please email us directly at:
                    </p>
                    <a 
                      href={`mailto:${submitError}?subject=${encodeURIComponent(`VVS Contact: ${TYPES.find(t => t.value === formData.type)?.label}`)}&body=${encodeURIComponent(formData.message)}`}
                      style={{ color: 'var(--vvs-cyan)', fontFamily: 'Space Grotesk', fontSize: '14px' }}
                    >
                      {submitError}
                    </a>
                  </div>
                )}
              </form>
            ) : (
              <div className="form-success" data-testid="contact-success" style={{
                padding: isMobile ? '40px 24px' : '60px 48px', 
                border: '1px solid rgba(0,180,255,0.15)',
                background: 'linear-gradient(180deg, rgba(10,22,40,0.9) 0%, rgba(10,22,40,0.7) 100%)', 
                textAlign: 'center', 
                opacity: 0,
                borderRadius: '4px',
              }}>
                {/* Success symbol */}
                <p style={{ 
                  fontFamily: 'Syne, sans-serif', 
                  fontWeight: 700, 
                  fontSize: isMobile ? '28px' : '36px', 
                  color: 'var(--vvs-cyan)', 
                  marginBottom: '20px',
                  letterSpacing: '0.1em',
                }}>
                  ✦
                </p>
                
                {/* Main title */}
                <p style={{ 
                  fontFamily: 'Syne, sans-serif', 
                  fontWeight: 700, 
                  fontSize: isMobile ? '18px' : '22px', 
                  textTransform: 'uppercase', 
                  color: '#F0F4F8', 
                  marginBottom: '16px',
                  letterSpacing: '0.08em',
                }}>
                  {t('contact.success_title')}
                </p>
                
                {/* Subtitle */}
                <p style={{ 
                  fontFamily: 'Space Grotesk', 
                  fontSize: isMobile ? '14px' : '15px', 
                  color: 'var(--vvs-muted)', 
                  lineHeight: 1.7, 
                  marginBottom: '32px',
                  maxWidth: '400px',
                  margin: '0 auto 32px',
                }}>
                  {t('contact.success_close')}
                </p>
                
                {/* Back to contact button */}
                <button 
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-back-btn"
                  className="btn-metallic"
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(240,244,248,0.15)',
                    color: 'var(--vvs-muted)',
                    fontFamily: 'Space Grotesk, sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    padding: '14px 28px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'var(--vvs-cyan)';
                    e.currentTarget.style.color = '#F0F4F8';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'rgba(240,244,248,0.15)';
                    e.currentTarget.style.color = 'var(--vvs-muted)';
                  }}
                >
                  [ {t('contact.back_to_contact') || 'Back to contact'} ]
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px', paddingTop: '40px', borderTop: '1px solid rgba(240,244,248,0.06)' }}>
            <a href="mailto:contact@vaguevisionsound.com" data-testid="contact-email-link"
              style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--vvs-muted)', textDecoration: 'none', fontSize: '14px', fontFamily: 'Space Grotesk', transition: 'color 0.3s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--vvs-cyan)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--vvs-muted)'}
            >
              <Mail size={15} /> contact@vaguevisionsound.com
            </a>
            <a href="https://www.instagram.com/vaguevisionsound/" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram"
              style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--vvs-muted)', textDecoration: 'none', fontSize: '14px', fontFamily: 'Space Grotesk', transition: 'color 0.3s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#F0F4F8'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--vvs-muted)'}
            >
              <Instagram size={15} /> @vaguevisionsound
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
